import React from 'react';
import { useLang } from './LangContext';

const sharedSections = {
  en: {
    title: 'About Pomodoro Flow',
    intro: 'Pomodoro Flow is a browser-based focus timer for people who want a simple work / break rhythm without creating an account or installing anything.',
    cards: [
      {
        title: 'What the site is for',
        body: [
          'The goal is straightforward: help you enter focused work blocks, take deliberate breaks, and keep the tool out of your way.',
          'It is intended for studying, writing, coding, reading, admin work, and similar tasks that benefit from a clean interval-based routine.',
        ],
      },
      {
        title: 'What the site stores',
        body: [
          'Your timer preferences such as work length, break length, interval count, countdown, and auto-start settings are stored locally in your browser.',
          'You can use the timer without creating an account, and the core timer remains usable even if marketing consent is not granted.',
        ],
      },
      {
        title: 'Who runs it',
        body: [
          'Pomodoro Flow is operated by Tobias Mai.',
          'The imprint, contact, and privacy pages are linked site-wide so visitors and reviewers can verify who is responsible for the site and how to get in touch.',
        ],
      },
      {
        title: 'How advertising is handled',
        body: [
          'Ads are clearly labeled and visually separated from the timer controls.',
          'The current product decision is that the ad container may appear when the page loads, can be closed by the user, and may reappear later during long-break or long-pause periods.',
          'That behavior is a review risk worth monitoring on mobile, but it is less problematic when the page also provides clear product information, legal pages, and a usable experience without ad interaction.',
        ],
      },
    ],
    riskTitle: 'Ad-review position',
    riskBody: [
      'The current ad-container behavior is not automatically disqualifying, but it is not zero-risk either. Sticky or immediately visible ad placements can attract stricter scrutiny if the page feels thin, confusing, or too ad-forward.',
      'The mitigation strategy is therefore to improve the surrounding page quality and trust signals without removing the required startup-visible + closeable behavior.',
    ],
    checklistTitle: 'Quality signals this page supports',
    checklist: [
      'Clear explanation of what Pomodoro Flow does and who it is for',
      'Visible operator identity and contact path',
      'Direct links to imprint, contact, and privacy information',
      'Plain-language explanation of local storage and consent-gated ads',
      'Honest statement that some ad-review uncertainty remains',
    ],
  },
  de: {
    title: 'Über Pomodoro Flow',
    intro: 'Pomodoro Flow ist ein browserbasierter Fokus-Timer für Menschen, die einen einfachen Arbeits-/Pausenrhythmus ohne Konto und ohne Installation wollen.',
    cards: [
      {
        title: 'Wofür die Seite gedacht ist',
        body: [
          'Das Ziel ist schlicht: konzentrierte Arbeitsblöcke unterstützen, bewusste Pausen fördern und dabei als Werkzeug nicht im Weg stehen.',
          'Die Seite ist gedacht für Lernen, Schreiben, Programmieren, Lesen, Verwaltungsaufgaben und ähnliche Tätigkeiten, die von klaren Intervallen profitieren.',
        ],
      },
      {
        title: 'Was die Seite speichert',
        body: [
          'Timer-Einstellungen wie Arbeitszeit, Pausenlänge, Intervallanzahl, Countdown und Auto-Start werden lokal im Browser gespeichert.',
          'Der Timer funktioniert ohne Konto, und die Kernfunktion bleibt auch ohne Marketing-Zustimmung nutzbar.',
        ],
      },
      {
        title: 'Wer dahintersteht',
        body: [
          'Pomodoro Flow wird von Tobias Mai betrieben.',
          'Impressum, Kontakt und Datenschutz sind seitenweit verlinkt, damit Besucher und Reviewer nachvollziehen können, wer verantwortlich ist und wie man Kontakt aufnimmt.',
        ],
      },
      {
        title: 'Wie Werbung gehandhabt wird',
        body: [
          'Werbung ist klar gekennzeichnet und visuell von Timer-Steuerung und Inhalt getrennt.',
          'Die aktuelle Produktentscheidung lautet: Der Werbe-Container darf beim Laden der Seite sichtbar sein, kann vom Nutzer geschlossen werden und später in langen Pausen erneut erscheinen.',
          'Dieses Verhalten bleibt ein gewisses Review-Risiko, vor allem mobil. Es ist aber deutlich vertretbarer, wenn die Seite zugleich klare Produktinformationen, Rechtstexte und eine brauchbare Nutzung ohne Anzeigen bietet.',
        ],
      },
    ],
    riskTitle: 'Einschätzung zur Anzeigen-Review',
    riskBody: [
      'Das aktuelle Verhalten des Werbe-Containers ist nicht automatisch problematisch, aber auch kein Null-Risiko. Sticky oder sofort sichtbare Anzeigen werden kritischer geprüft, wenn die Seite dünn, unklar oder zu werbelastig wirkt.',
      'Die passende Gegenmaßnahme ist daher: die umgebende Seitenqualität und Vertrauenssignale verbessern, ohne das geforderte sichtbar-beim-Start + schließbare Verhalten zu entfernen.',
    ],
    checklistTitle: 'Qualitätssignale, die diese Seite stärkt',
    checklist: [
      'Klare Erklärung, was Pomodoro Flow macht und für wen es gedacht ist',
      'Sichtbare Betreiberidentität und Kontaktmöglichkeit',
      'Direkte Links zu Impressum, Kontakt und Datenschutz',
      'Verständliche Erklärung zu lokaler Speicherung und zustimmungsabhängigen Ads',
      'Ehrlicher Hinweis, dass bei der Anzeigenprüfung Restunsicherheit bleibt',
    ],
  },
};

function About() {
  const { lang } = useLang();
  const copy = sharedSections[lang] || sharedSections.en;

  return (
    <div className="info-page-shell">
      <div className="info-page about-page">
        <section className="content-card info-card info-hero-card">
          <p className="eyebrow">Pomodoro Flow</p>
          <h1>{copy.title}</h1>
          <p className="lead info-intro">{copy.intro}</p>
        </section>

        <div className="info-page-stack">
          {copy.cards.map((card) => (
            <section key={card.title} className="content-card info-card">
              <h2>{card.title}</h2>
              {card.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          <section className="content-card info-card">
            <h2>{copy.riskTitle}</h2>
            {copy.riskBody.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>

          <section className="content-card info-card">
            <h2>{copy.checklistTitle}</h2>
            <ul className="info-list">
              {copy.checklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export default About;
